import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { DatePickerInputBottomSheet } from "@src/appV2/redesign/DateTime/DatePickerInputBottomSheet";
import { Input, Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { type FieldErrors } from "react-hook-form";

import { FORM_FIELDS } from "../constants";
import { type ReferenceFormData } from "../types";
import { formatMonthYear } from "../utils/formatters";
import { getReferenceFormFieldErrorId, getReferenceFormFieldId } from "../utils/formHelpers";

interface EmploymentDateFieldsProps {
  formId: string;
  employmentStartDate: Date | undefined;
  employmentEndDate: Date | undefined;
  errors: FieldErrors<ReferenceFormData>;
  touchedFields: Partial<Record<keyof ReferenceFormData, boolean>>;
  startDateModalState: UseModalState;
  endDateModalState: UseModalState;
  onStartDateConfirm: (date: Date) => void;
  onEndDateConfirm: (date: Date) => void;
}

export function EmploymentDateFields(props: EmploymentDateFieldsProps) {
  const {
    formId,
    employmentStartDate,
    employmentEndDate,
    errors,
    touchedFields,
    startDateModalState,
    endDateModalState,
    onStartDateConfirm,
    onEndDateConfirm,
  } = props;

  return (
    <>
      <Box component="fieldset" sx={{ border: "none", padding: 0, margin: 0 }}>
        <Box component="legend">
          <Text inline variant="body1">
            Dates of Employment
          </Text>
        </Box>
        <Stack direction="row" spacing={2} sx={{ marginTop: 2 }}>
          <Stack flex={1}>
            <Input
              readOnly
              id={getReferenceFormFieldId(formId, FORM_FIELDS.EMPLOYMENT_START_DATE)}
              name={FORM_FIELDS.EMPLOYMENT_START_DATE}
              placeholder="MM/YYYY"
              value={
                isDefined(employmentStartDate)
                  ? formatMonthYear(
                      employmentStartDate.getMonth(),
                      employmentStartDate.getFullYear()
                    )
                  : ""
              }
              error={
                isDefined(touchedFields.employmentStartDate) &&
                isDefined(errors.employmentStartDate)
              }
              inputProps={{
                "aria-label": "Employment Start Date",
                "aria-describedby": isDefined(errors.employmentStartDate)
                  ? getReferenceFormFieldErrorId(
                      getReferenceFormFieldId(formId, FORM_FIELDS.EMPLOYMENT_START_DATE)
                    )
                  : undefined,
              }}
              onClick={() => {
                startDateModalState.openModal();
              }}
            />
            {isDefined(touchedFields.employmentStartDate) &&
              isDefined(errors.employmentStartDate) && (
                <Text
                  id={getReferenceFormFieldErrorId(
                    getReferenceFormFieldId(formId, FORM_FIELDS.EMPLOYMENT_START_DATE)
                  )}
                  variant="caption"
                  color="error"
                  sx={{ paddingLeft: 1, marginTop: 1 }}
                  role="alert"
                >
                  {errors.employmentStartDate.message}
                </Text>
              )}
          </Stack>
          <Text variant="body1" sx={{ minWidth: "fit-content", alignSelf: "center" }}>
            -
          </Text>
          <Stack flex={1}>
            <Input
              readOnly
              id={getReferenceFormFieldId(formId, FORM_FIELDS.EMPLOYMENT_END_DATE)}
              name={FORM_FIELDS.EMPLOYMENT_END_DATE}
              placeholder="MM/YYYY"
              value={
                isDefined(employmentEndDate)
                  ? formatMonthYear(employmentEndDate.getMonth(), employmentEndDate.getFullYear())
                  : ""
              }
              error={
                isDefined(touchedFields.employmentEndDate) && isDefined(errors.employmentEndDate)
              }
              inputProps={{
                "aria-label": "Employment End Date",
                "aria-describedby": isDefined(errors.employmentEndDate)
                  ? getReferenceFormFieldErrorId(
                      getReferenceFormFieldId(formId, FORM_FIELDS.EMPLOYMENT_END_DATE)
                    )
                  : undefined,
              }}
              onClick={() => {
                endDateModalState.openModal();
              }}
            />
            {isDefined(touchedFields.employmentEndDate) && isDefined(errors.employmentEndDate) && (
              <Text
                id={getReferenceFormFieldErrorId(
                  getReferenceFormFieldId(formId, FORM_FIELDS.EMPLOYMENT_END_DATE)
                )}
                variant="caption"
                color="error"
                sx={{ paddingLeft: 1, marginTop: 1 }}
                role="alert"
              >
                {errors.employmentEndDate.message}
              </Text>
            )}
          </Stack>
        </Stack>
      </Box>

      <DatePickerInputBottomSheet
        disableFuture
        shouldAutoConfirmOnSelect
        shouldHideCloseButton
        views={["year", "month"]}
        openTo="year"
        title={
          <Text semibold textAlign="center" variant="h5" sx={{ marginBottom: 4 }}>
            Employment start
          </Text>
        }
        modalState={startDateModalState}
        initialDate={employmentStartDate}
        maxDate={employmentEndDate}
        onConfirm={onStartDateConfirm}
      />
      <DatePickerInputBottomSheet
        disableFuture
        shouldAutoConfirmOnSelect
        shouldHideCloseButton
        views={["year", "month"]}
        openTo="year"
        title={
          <Text semibold textAlign="center" variant="h5" sx={{ marginBottom: 4 }}>
            Employment end
          </Text>
        }
        modalState={endDateModalState}
        initialDate={employmentEndDate}
        minDate={employmentStartDate}
        onConfirm={onEndDateConfirm}
      />
    </>
  );
}
